// The building's time zone on the add and edit forms.
//
// THE ZONE COMES FROM WHERE THE BUILDING IS. Drop the pin and the field fills
// itself in from the pin's location; the server derives it again on save and is
// the authority. The browser's own zone — where the PERSON is — is only what a
// new building shows until it has a pin.
//
// The field stays a plain text box that can be typed into. Typing is an
// explicit choice (`manual`): it is sent as an override, wins over the pin, and
// is how a building on a border, or one saved without a map, gets its zone.
import { deviceZone } from '../../../../function-utils/building-time';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ZoneAtPoint } from '../../../../api/geo-client';
import { GeoAccessContext } from './geo-access';
import {
  FieldHint,
  InputContainer,
  Label,
  LocationClearButton,
  StyledBuildingInput,
} from './dashboard-buildings.styled';

type Mode = 'auto' | 'manual';

/** properties.timezone_source, as the API returns it. */
export type ZoneSource = 'coordinates' | 'city' | 'manual' | 'browser' | 'default';

// The device's own zone — the fallback for a building with no pin. Read through
// the one function in the app allowed to ask the platform for it.
const browserZone = (): string => deviceZone();

interface Options {
  /** The zone the building is stored with; '' for a building being created. */
  savedZone: string;
  /** Where the stored zone came from; undefined for a building being created. */
  savedSource?: ZoneSource | null;
}

export interface BuildingZoneState {
  /** What the field shows and, where a zone is sent, what is sent. */
  zone: string;
  mode: Mode;
  /** The zone under the pin as last reported by the picker, if any. */
  pinZone: string | null;
  /** The pin sits where there is no single zone; the person has to choose. */
  needsChoice: boolean;
  candidates: string[];
  savedZone: string;
  savedSource: ZoneSource | null;
  isNew: boolean;
  /** Pass to BuildingLocationPicker's onZoneHint. */
  onZoneHint: (hint: ZoneAtPoint | null) => void;
  choose: (zone: string) => void;
  followPin: () => void;
  /** The zone fields of the save request (multipart form fields). */
  fields: () => Record<string, string>;
}

export const useBuildingZone = ({ savedZone, savedSource }: Options): BuildingZoneState => {
  const isNew = !savedZone;
  const [mode, setMode] = useState<Mode>('auto');
  const [manualZone, setManualZone] = useState('');
  const [hint, setHint] = useState<ZoneAtPoint | null>(null);

  // A different building was loaded into the form: start again from its zone.
  useEffect(() => {
    setMode('auto');
    setManualZone('');
    setHint(null);
  }, [savedZone]);

  const pinZone = hint ? hint.timezone : null;
  const needsChoice = Boolean(hint && !hint.timezone);
  const fallback = isNew ? browserZone() : savedZone;
  const zone = mode === 'manual' ? manualZone : pinZone || fallback;

  const choose = useCallback((next: string) => {
    setMode('manual');
    setManualZone(next);
  }, []);
  const followPin = useCallback(() => {
    setMode('auto');
    setManualZone('');
  }, []);

  const fields = useCallback((): Record<string, string> => {
    const typed = manualZone.trim();
    if (mode === 'manual' && typed) {
      // An explicit choice: it wins over the pin.
      return { timezone: typed, timezone_override: '1', timezone_source: 'manual' };
    }

    // A new building always leaves with a zone to fall back on: the server uses
    // the pin when there is one and this otherwise. An existing building sends
    // nothing — the server re-derives from a moved pin by itself, and a zone
    // nobody touched must not be re-posted as though somebody had chosen it.
    return isNew ? { timezone: browserZone() || 'UTC' } : {};
  }, [mode, manualZone, isNew]);

  return {
    zone,
    mode,
    pinZone,
    needsChoice,
    candidates: hint ? hint.candidates : [],
    savedZone,
    savedSource: savedSource || null,
    isNew,
    onZoneHint: setHint,
    choose,
    followPin,
    fields,
  };
};

// One line under the field saying where the value came from, in plain words.
const explain = (state: BuildingZoneState): string => {
  const { mode, pinZone, needsChoice, isNew, savedZone, savedSource, zone } = state;

  if (mode === 'manual' && !zone.trim()) {
    return 'Type a time zone, such as America/New_York.';
  }
  if (mode === 'manual') {
    return !isNew && zone.trim() && zone.trim() !== savedZone
      ? 'Chosen by hand. Saving will ask you to confirm the change.'
      : 'Chosen by hand.';
  }
  if (needsChoice) {
    return 'That spot on the map has no single time zone, so it is still the one shown. Choose the right one.';
  }
  if (pinZone) {
    if (!isNew && pinZone !== savedZone) {
      return `The pin is in ${pinZone}; this building is on ${savedZone}. Saving will ask you to confirm the change.`;
    }

    return 'Set from the building’s location on the map.';
  }
  if (isNew) {
    return 'Taken from this browser for now. Pin the building on the map and it is set from the building’s location.';
  }
  switch (savedSource) {
    case 'coordinates':
      return 'Set from the building’s location on the map.';
    case 'city':
      return 'Set from the city chosen at signup.';
    case 'manual':
      return 'Chosen by hand.';
    default:
      return 'Not confirmed yet. Pin the building on the map to set it from its location.';
  }
};

interface FieldProps {
  /** 'new-building' | 'edit-building' — the ids the forms and tests already use. */
  idPrefix: string;
  state: BuildingZoneState;
  onDirty?: () => void;
  /**
   * The form is being read, not edited (a property manager opening a building:
   * building:read without building:edit). The list of zones is only ever a
   * suggestion for typing, and GET /geo/zones is for people who may place
   * buildings — asked for here it answered 403 on every open.
   */
  readOnly?: boolean;
}

export const BuildingTimeZoneField: React.FC<FieldProps> = ({
  idPrefix,
  state,
  onDirty,
  readOnly = false,
}) => {
  const [zones, setZones] = useState<string[]>([]);
  // Staff routes, or the signed-out ones on the signup wizard (geo-access.tsx).
  const geoAccess = useContext(GeoAccessContext);

  useEffect(() => {
    if (readOnly) return undefined;
    let alive = true;
    geoAccess.zones().then(list => {
      if (alive) setZones(list);
    });

    return () => {
      alive = false;
    };
  }, [readOnly, geoAccess]);

  // The candidates for an ambiguous pin lead the list, so they are what the
  // browser offers first.
  const options = useMemo(
    () => Array.from(new Set([...state.candidates, ...zones])),
    [state.candidates, zones]
  );
  const listId = `${idPrefix}-time-zones`;

  return (
    <InputContainer>
      <Label htmlFor={`${idPrefix}-timezone`}>Time zone</Label>
      {/* A <datalist>, not a <select>: the field must still take a value on an
          engine with no list to offer, and the server refuses any name it does
          not know either way. */}
      <StyledBuildingInput
        id={`${idPrefix}-timezone`}
        type="text"
        name="timezone"
        autoComplete="off"
        list={options.length ? listId : undefined}
        value={state.zone}
        placeholder="e.g. America/New_York"
        onChange={e => {
          onDirty?.();
          state.choose(e.target.value);
        }}
      />
      {options.length > 0 && (
        <datalist id={listId}>
          {options.map(zone => (
            <option key={zone} value={zone} />
          ))}
        </datalist>
      )}
      <FieldHint>
        The building’s own clock: every time shown for this building, and when
        its desk day ends. {explain(state)}
      </FieldHint>
      {/* A pin on a border or a coast: the zones it could be, as buttons. A
          <datalist> alone is not enough here — a phone shows two or three of
          its entries in the keyboard bar, and some browsers show none. */}
      {state.needsChoice && state.mode === 'auto'
        ? state.candidates.map(candidate => (
            <LocationClearButton
              key={candidate}
              type="button"
              onClick={() => {
                onDirty?.();
                state.choose(candidate);
              }}
            >
              Use {candidate}
            </LocationClearButton>
          ))
        : null}
      {state.mode === 'manual' && (state.pinZone || !state.isNew) ? (
        <LocationClearButton
          type="button"
          onClick={() => {
            onDirty?.();
            state.followPin();
          }}
        >
          {state.pinZone ? 'Use the map pin’s time zone' : 'Undo this change'}
        </LocationClearButton>
      ) : null}
    </InputContainer>
  );
};
